// Where a container with a max width sits when its parent is wider: at the start, in the
// centre or at the end of the row.
//
// The authored side margins are a floor, never replaced: the alignment only shares out the
// space left once the max width actually caps the box. So a max width the box does not
// reach changes nothing — on a scaled page (always the design width) or a phone, the box
// keeps the margins it was given. The component keeps its authored `margin` inline and adds
// the two placed sides as custom properties (`max()` / `min()` of the authored margin and
// the free space) with MAX_WIDTH_ALIGNED_CLASS, which render/assets/max-width-align.css
// turns into `margin-left` / `margin-right` — behind `@supports`, because the sides often
// wrap a token's `var()`: an engine without `max()` (Chrome before 79) would accept such a
// declaration and compute it to 0, so it must skip the rule and keep the authored margin.
// Physical sides, never `margin-inline`: Safari before 15 drops a logical shorthand that
// holds `var()` (docs/ISSUE_LOG.md).

export const MAX_WIDTH_ALIGNS = ['start', 'center', 'end'] as const
export type MaxWidthAlign = (typeof MAX_WIDTH_ALIGNS)[number]

export function isMaxWidthAlign(value: unknown): value is MaxWidthAlign {
  return (MAX_WIDTH_ALIGNS as readonly unknown[]).includes(value)
}

/** The class that applies the placed sides — see render/assets/max-width-align.css. */
export const MAX_WIDTH_ALIGNED_CLASS = 'max-width-aligned'
const MARGIN_LEFT_VAR = '--max-width-margin-left'
const MARGIN_RIGHT_VAR = '--max-width-margin-right'

// What the placed sides can do arithmetic on: a number with px / rem / em / %, or a token.
// Anything else (a unitless size) would make the side compute to 0, so it is not aligned.
const CSS_LENGTH = /^(?:-?(?:\d+(?:\.\d+)?|\.\d+)(?:px|rem|em|%)|var\(--[\w-]+\))$/

/** The authored side margins as CSS lengths — physical, as the `margin` shorthand renders them. */
export interface SideMargins {
  left: string
  right: string
}

/**
 * The placed sides of a box with a max width. Centred, each side is the larger of its
 * authored margin and half the space beyond the max width — held back by the other side's
 * margin, so a lopsided pair still leaves the box its full max width when it fits. Start /
 * end pin the box to that side (the right-hand side is the start under RTL) at its
 * authored margin; the free side takes whatever the max width leaves over.
 */
export function alignedSideMargins(
  sides: SideMargins,
  maxWidth: string,
  align: MaxWidthAlign,
  rtl: boolean,
): SideMargins {
  const { left, right } = sides
  // One side's room when the box is at its max width and the other keeps its margin.
  const room = (other: string) => `100% - ${maxWidth} - ${other}`
  if (align === 'center') {
    const half = `(100% - ${maxWidth}) / 2`
    return {
      left: `max(${left}, min(${half}, ${room(right)}))`,
      right: `max(${right}, min(${half}, ${room(left)}))`,
    }
  }
  return (align === 'start') !== rtl
    ? { left, right: `max(${right}, ${room(left)})` }
    : { left: `max(${left}, ${room(right)})`, right }
}

/**
 * The margin style of a box: the authored `margin` shorthand (as getSpacingStyleData
 * renders it) and — when it has a max width and an alignment — the placed sides. A box
 * with them takes MAX_WIDTH_ALIGNED_CLASS too (`isMaxWidthAligned`).
 */
export function maxWidthMarginStyle(
  margin: string | undefined,
  sides: SideMargins,
  maxWidth: string | undefined,
  align: MaxWidthAlign | undefined,
  rtl: boolean,
): Record<string, string> {
  const style: Record<string, string> = {}
  if (margin) style.margin = margin
  // getSpacingStyleData writes an empty side as a unitless `0`, which a sum does not take.
  const left = sides.left === '0' ? '0px' : sides.left
  const right = sides.right === '0' ? '0px' : sides.right
  if (maxWidth && align && [maxWidth, left, right].every((value) => CSS_LENGTH.test(value))) {
    const placed = alignedSideMargins({ left, right }, maxWidth, align, rtl)
    style[MARGIN_LEFT_VAR] = placed.left
    style[MARGIN_RIGHT_VAR] = placed.right
  }
  return style
}

/** Whether a style from maxWidthMarginStyle places the box, so it needs MAX_WIDTH_ALIGNED_CLASS. */
export function isMaxWidthAligned(style: object): boolean {
  return MARGIN_LEFT_VAR in style
}
